<template>
    <div
        :class="{ 'opacity-0': !isRendererReady }"
        class="transition-opacity duration-300">
        <div
            ref="wrap"
            class="three relative transform-gpu"></div>

        <ClientOnly>
            <Teleport to="#teleports">
                <ModelTooltip
                    :is-hover-model
                    :tab
                    :top="tooltipPos.y"
                    :left="tooltipPos.x" />
            </Teleport>
        </ClientOnly>
    </div>
</template>

<script setup lang="ts">
import { useRafFn, useWindowSize } from '@vueuse/core'
import gsap from 'gsap'
import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js'
import { PMREMGenerator } from 'three/src/extras/PMREMGenerator.js'
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

import type { TabInfo } from '~/components/particles/first-screen/tab.types'
import ModelTooltip from '~/components/particles/model-tooltip/ModelTooltip.vue'

/* ------------------------------------------------------------------ constants */
const OBJECTS = {
    'radioguides-tab': {
        pos: [0, 0, 0] as THREE.Vector3Tuple,
        path: '/assets/3d/radioguide/radiogid-4.glb',
        rotate: 0,
        camPos: [-1.5, 0, 0] as THREE.Vector3Tuple,
        lookAt: [0, 0, 0] as THREE.Vector3Tuple,
    },
    'cabs-tab': {
        pos: [0, 0, 0] as THREE.Vector3Tuple,
        path: '/assets/3d/cabina/Cabina.glb',
        rotate: 0,
        camPos: [-4, 2, -4] as THREE.Vector3Tuple,
        lookAt: [0, 1, 0] as THREE.Vector3Tuple,
    },
} as const

type ModelKey = keyof typeof OBJECTS

const HDR_PATH = '/assets/3d/hdr-1.hdr'

// props
const props = defineProps<{
    controlType?: 'mouse' | 'button'
    model: ModelKey
    tab: TabInfo
}>()
const emit = defineEmits<{
    ready: []
    settled: [outcome: 'ready' | 'fallback']
}>()
const controlType = props.controlType ?? 'mouse'
const router = useRouter()
const { $reportFrontendError } = useNuxtApp()

// refs (DOM)
const wrap = ref<HTMLDivElement | null>(null)
const isRendererReady = ref(false)

// Three.js core objects (создаём на клиенте, чтобы не падать на SSR)
let scene: THREE.Scene
let camera: THREE.PerspectiveCamera
let renderer: THREE.WebGLRenderer | null = null
let controls: OrbitControls | null = null
let modelObject: THREE.Object3D | null = null
let isUnmounted = false
let generation = 0
let failed = false
let environmentTarget: THREE.WebGLRenderTarget | null = null

/* ------------------------------------------------------------------ helper */
function createCore() {
    scene = new THREE.Scene()
    camera = new THREE.PerspectiveCamera(
        50,
        width.value / height.value,
        0.1,
        1000
    )
}

async function loadModel(token: number) {
    const { path, pos, rotate } = OBJECTS[props.model]
    const gltf = await new GLTFLoader().loadAsync(path)
    if (isUnmounted || failed || token !== generation) {
        disposeObject(gltf.scene)
        return
    }
    modelObject = gltf.scene
    modelObject.position.set(...pos)
    modelObject.rotation.y = rotate
    scene.add(modelObject)
}

async function initEnv(token: number) {
    if (props.model !== 'cabs-tab') return
    const hdr = await new RGBELoader().loadAsync(HDR_PATH)
    if (isUnmounted || failed || token !== generation) {
        hdr.dispose()
        return
    }
    hdr.mapping = THREE.CubeUVReflectionMapping
    let pmrem: PMREMGenerator | undefined
    try {
        pmrem = new PMREMGenerator(renderer!)
        environmentTarget = pmrem.fromEquirectangular(hdr)
        scene.environment = environmentTarget.texture
    } finally {
        hdr.dispose()
        pmrem?.dispose()
    }
}

function initLights() {
    const ambient = new THREE.AmbientLight(0x404040, 45)
    scene.add(ambient)

    const { pos } = OBJECTS[props.model]
    const dir = new THREE.DirectionalLight(0xffffff, 1)
    dir.position.set(pos[0] - 1.5, pos[1] + 1, pos[2])
    scene.add(dir)
}

function setupRenderer() {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    renderer.setClearColor(0x000000, 0)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.outputColorSpace = THREE.SRGBColorSpace
    renderer.toneMapping = THREE.NeutralToneMapping
    renderer.toneMappingExposure = 1
    renderer.physicallyCorrectLights = true
    renderer.domElement.addEventListener('mousemove', handleCanvasMouseMove)
    renderer.domElement.addEventListener('mouseleave', handleCanvasMouseLeave)
    renderer.domElement.addEventListener('click', handleCanvasClick)
    wrap.value?.appendChild(renderer.domElement)
}

function setupControls() {
    if (!renderer) return
    controls = new OrbitControls(camera, renderer.domElement)
    controls.enablePan = false

    controls.enableZoom = false

    controls.dampingFactor = 0.08
    const { camPos, lookAt } = OBJECTS[props.model]
    camera.position.set(...camPos)
    controls.target.set(...lookAt)
    controls.update()

    updateCameraAdaptive()
}

/* ------------------------------------------------------------------ mouse tilt */
const mouse = { x: 0, y: 0 }

function applyMouseTilt() {
    if (!modelObject) return
    const { innerWidth: w, innerHeight: h } = window
    const maxTilt = 0.2
    const maxRotY = 1
    const tiltX = ((mouse.y - h / 2) / (h / 2)) * maxTilt
    const tiltZ = (-(mouse.x - w / 2) / (w / 2)) * maxTilt
    const rotY = ((mouse.x - w / 2) / (w / 2)) * maxRotY
    gsap.to(modelObject.rotation, {
        x: tiltX,
        y: rotY,
        z: tiltZ,
        duration: 1.25,
        ease: 'power2.out',
    })
}

function handleWindowMouseMove(e: MouseEvent): void {
    if (controlType !== 'mouse') return

    mouse.x = e.clientX
    mouse.y = e.clientY
}

/* ------------------------------------------------------------------ mouse on model */
const raycaster = new THREE.Raycaster()
const pointer = new THREE.Vector2()
const tooltipPos = ref({ x: 0, y: 0 })

const isHoverModel = ref(false)
const hasMouseMoved = ref(false)
let isMouseOverCanvas = false

function updatePointer(e: MouseEvent): void {
    if (!renderer) return

    const rect = renderer.domElement.getBoundingClientRect()
    pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1
}

function handleCanvasMouseMove(e: MouseEvent): void {
    hasMouseMoved.value = true
    tooltipPos.value.x = e.clientX
    tooltipPos.value.y = e.clientY
    isMouseOverCanvas = true

    if (controlType === 'mouse' && modelObject) {
        mouse.x = e.clientX
        mouse.y = e.clientY
    }

    updatePointer(e)
}

function handleCanvasMouseLeave(): void {
    isMouseOverCanvas = false
    resetHover()
}

function handleCanvasClick(e: MouseEvent): void {
    if (!props.tab.to || !renderer || !modelObject) return

    updatePointer(e)
    raycaster.setFromCamera(pointer, camera)

    if (raycaster.intersectObject(modelObject, true).length) {
        router.push(props.tab.to)
    }
}

function checkIntersects() {
    if (!modelObject || !hasMouseMoved.value || !isMouseOverCanvas) {
        isHoverModel.value = false
        if (renderer) renderer.domElement.style.cursor = ''
        return
    }

    raycaster.setFromCamera(pointer, camera)
    const intersects = raycaster.intersectObject(modelObject, true)

    isHoverModel.value = intersects.length > 0
    if (renderer) {
        renderer.domElement.style.cursor =
            isHoverModel.value && props.tab.to ? 'pointer' : ''
    }
}

function resetHover() {
    isHoverModel.value = false
    if (renderer) renderer.domElement.style.cursor = ''
    tooltipPos.value.x = -9999
    tooltipPos.value.y = -9999
}

watch(
    () => props.model,
    () => {
        cleanup()
        resetHover()
        void initialize()
    }
)

/* ------------------------------------------------------------------ resize */
const { width, height } = useWindowSize()

watch([width, height], () => {
    if (!renderer) return
    camera.aspect = width.value / height.value
    camera.updateProjectionMatrix()
    renderer.setSize(width.value, height.value)

    updateCameraAdaptive()
})

function updateCameraAdaptive() {
    if (!camera) return
    const { camPos } = OBJECTS[props.model]

    const breakpoint = 1024
    const sensitivity = 0.25

    let factor = 1

    if (width.value > breakpoint) {
        const ratio = width.value / breakpoint
        factor = 1 + (ratio - 1) * sensitivity
    }

    camera.position.set(
        camPos[0] * factor,
        camPos[1] * factor,
        camPos[2] * factor
    )

    if (controls) {
        controls.update()
    }
}

/* ------------------------------------------------------------------ raf */
let stopRaf: () => void = () => {}

function startLoop() {
    const raf = useRafFn(() => {
        try {
            controls?.update()
            applyMouseTilt()
            checkIntersects()
            renderer?.render(scene, camera)
        } catch {
            fallback()
        }
    })
    stopRaf = raf.pause
}

/* ------------------------------------------------------------------ lifecycle */
function disposeObject(root: THREE.Object3D) {
    const geometries = new Set<THREE.BufferGeometry>()
    const materials = new Set<THREE.Material>()
    const textures = new Set<THREE.Texture>()
    root.traverse((object) => {
        const mesh = object as THREE.Mesh
        if (mesh.geometry) geometries.add(mesh.geometry)
        const values = Array.isArray(mesh.material)
            ? mesh.material
            : [mesh.material]
        for (const material of values) {
            if (!material) continue
            materials.add(material)
            for (const value of Object.values(material)) {
                if (value instanceof THREE.Texture) textures.add(value)
            }
        }
    })
    textures.forEach((texture) => texture.dispose())
    materials.forEach((material) => material.dispose())
    geometries.forEach((geometry) => geometry.dispose())
}

function cleanup() {
    stopRaf()
    stopRaf = () => {}
    window.removeEventListener('mousemove', handleWindowMouseMove)
    if (modelObject) gsap.killTweensOf(modelObject.rotation)
    renderer?.domElement.removeEventListener('mousemove', handleCanvasMouseMove)
    renderer?.domElement.removeEventListener(
        'mouseleave',
        handleCanvasMouseLeave
    )
    renderer?.domElement.removeEventListener('click', handleCanvasClick)
    renderer?.domElement.remove()
    controls?.dispose()
    controls = null
    renderer?.dispose()
    renderer = null
    if (scene) {
        disposeObject(scene)
        scene.environment = null
        scene.clear()
    }
    environmentTarget?.dispose()
    environmentTarget = null
    modelObject = null
    isRendererReady.value = false
}

function fallback() {
    if (isUnmounted || failed) return
    failed = true
    cleanup()
    resetHover()
    emit('settled', 'fallback')
    // Use the existing allowlisted transport; never send a raw exception/URL.
    try {
        void Promise.resolve($reportFrontendError({ type: 'runtime' })).catch(
            () => {}
        )
    } catch {
        // Diagnostics are optional too (for example, a blocked beacon transport).
    }
}

async function initialize() {
    if (isUnmounted || typeof window === 'undefined') return
    const token = ++generation
    failed = false
    try {
        createCore()
        window.addEventListener('mousemove', handleWindowMouseMove)
        setupRenderer()
        setupControls()
        initLights()
        await Promise.all([initEnv(token), loadModel(token)])
        if (isUnmounted || failed || token !== generation) return
        await nextTick()
        if (isUnmounted || failed || token !== generation || !renderer) return
        renderer.setSize(window.innerWidth, window.innerHeight)
        renderer.render(scene, camera)
        isRendererReady.value = true
        await nextTick()
        if (isUnmounted || failed || token !== generation) return
        emit('ready')
        emit('settled', 'ready')
        startLoop()
    } catch {
        if (token === generation) fallback()
    }
}

onMounted(initialize)
onUnmounted(() => {
    isUnmounted = true
    generation++
    cleanup()
})
</script>

<style scoped>
.three {
    width: 100%;
    height: 100vh;
}

.three canvas {
    pointer-events: auto;
}
</style>
